import type { SlideTemplateEnum } from "@/generated/graphql";
import type { SlideTemplate } from "@ai-teach/types";

/**
 * Web-side template config: which templates authors may create and how they are labelled.
 * The template vocabulary itself — the enum, the legacy pair mapping, `resolveSlideTemplate`
 * and `isFreeformTemplate` — lives in @ai-teach/types so the API and mobile share it.
 *
 * The editor authors by template alone — `type`/`knowledgeArea` are derived server-side.
 */

/**
 * The generated GraphQL enum is nominal while `SlideTemplate` is a string union of the same
 * values, so mutation variables need one bridge. This is the only cast between the two.
 */
export function toTemplateEnum(template: SlideTemplate): SlideTemplateEnum {
  return template as SlideTemplateEnum;
}

/** Templates authors can create, in picker order. `audioRevealPrompt` and `wordCanvas` are
 *  intentionally absent — both exist only in legacy data and were never offered for creation. */
export const CREATABLE_TEMPLATES: SlideTemplate[] = [
  "vocabulary",
  "listenRepeat",
  "conversation",
  "videoQuestion",
  "passageReading",
  "soundToSpelling",
  "spellingToSound",
  "freeformCanvas",
  "handWriting",
];

/** Templates the generator can write, mirroring the flows the API registers. A section built on
 *  any other template drafts nothing, so the generator offers these alone. */
export const GENERATABLE_TEMPLATES: SlideTemplate[] = [
  "vocabulary",
  "listenRepeat",
  "conversation",
  "passageReading",
  "spellingToSound",
  "freeformCanvas",
];

export const TEMPLATE_LABELS: Record<SlideTemplate, string> = {
  vocabulary: "Vocabulary",
  listenRepeat: "Listen & Repeat",
  conversation: "AI Conversation",
  videoQuestion: "Video Questions",
  passageReading: "Passage Reading",
  soundToSpelling: "Sound to Spelling",
  spellingToSound: "Spelling to Sound",
  freeformCanvas: "Freeform Canvas",
  wordCanvas: "Word Canvas",
  audioRevealPrompt: "Audio Reveal Prompt",
  handWriting: "Hand Writing",
};

export function getTemplateLabel(template: SlideTemplate | null | undefined): string {
  return template ? TEMPLATE_LABELS[template] : "—";
}
